import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { ListItemIcon, ListItemText, Menu } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ExcelIcon } from 'assets/icons/fileType/excel.svg';
import { ReactComponent as PPTIcon } from 'assets/icons/fileType/ppt.svg';
import { ReactComponent as TextIcon } from 'assets/icons/fileType/txt.svg';
import { ReactComponent as WordIcon } from 'assets/icons/fileType/word.svg';

import { SortMenuItem } from './styled';

const FILE_GROUPS = [
  {
    value: 'Word',
    label: 'Word',
    icon: <WordIcon style={{ width: 20, height: 20 }} />,
  },
  {
    value: 'Excel',
    label: 'Excel',
    icon: <ExcelIcon style={{ width: 20, height: 20 }} />,
  },
  {
    value: 'PowerPoint',
    label: 'PowerPoint',
    icon: <PPTIcon style={{ width: 20, height: 20 }} />,
  },
  {
    value: 'Text',
    label: 'Text',
    icon: <TextIcon style={{ width: 20, height: 20 }} />,
  },
  {
    value: 'PDF',
    label: 'PDF',
    icon: <PictureAsPdfIcon sx={{ width: 20, height: 20, color: '#e53935' }} />,
  },
  {
    value: 'Media',
    label: 'Media',
    icon: <PhotoLibraryOutlinedIcon sx={{ width: 20, height: 20, color: '#5A5AF2' }} />,
  },
];

interface TypeFilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  showFolders?: boolean;
  style?: React.CSSProperties;
}

export const TypeFilterDropdown = ({
  value,
  onChange,
  showFolders = true,
  style,
}: TypeFilterDropdownProps) => {
  const [mainAnchor, setMainAnchor] = useState<null | HTMLElement>(null);
  const [subAnchor, setSubAnchor] = useState<null | HTMLElement>(null);
  const filesItemRef = useRef<HTMLLIElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenMain = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMainAnchor(e.currentTarget);
  };

  const handleCloseAll = () => {
    setMainAnchor(null);
    setSubAnchor(null);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    handleCloseAll();
  };

  const handleFilesMouseEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setSubAnchor(filesItemRef.current);
  };

  const handleFilesMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setSubAnchor(null), 150);
  };

  const handleSubMouseEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  const handleSubMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setSubAnchor(null), 150);
  };

  const { t } = useTranslation();

  const isFileGroup = !!value && value !== 'Folders';

  return (
    <>
      <button
        onClick={handleOpenMain}
        style={{
          height: '44px',
          minWidth: '150px',
          background: '#fff',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          padding: '0 12px',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          color: '#000',
          fontFamily: 'inherit',
          justifyContent: 'center',
          ...style,
        }}
      >
        {value || t('resources.documentCategories.typeLabel')}
        <KeyboardArrowDownIcon sx={{ fontSize: 18, ml: 'auto', pl: '4px' }} />
      </button>

      <Menu
        anchorEl={mainAnchor}
        open={Boolean(mainAnchor)}
        onClose={handleCloseAll}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            mt: 1,
            minWidth: 140,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <SortMenuItem selected={value === ''} onClick={() => handleSelect('')}>
          <ListItemText>{t('resources.documentCategories.allLabel')}</ListItemText>
        </SortMenuItem>

        <SortMenuItem
          ref={filesItemRef}
          selected={isFileGroup}
          onMouseEnter={handleFilesMouseEnter}
          onMouseLeave={handleFilesMouseLeave}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
        >
          <ListItemText>{t('resources.documentCategories.fileLabel')}</ListItemText>
          <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </SortMenuItem>

        {showFolders && (
          <SortMenuItem selected={value === 'Folders'} onClick={() => handleSelect('Folders')}>
            <ListItemText>{t('resources.documentCategories.folderLabel')}</ListItemText>
          </SortMenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={subAnchor}
        open={Boolean(subAnchor)}
        onClose={() => setSubAnchor(null)}
        PaperProps={{
          sx: { borderRadius: '8px', boxShadow: '0px 4px 20px rgba(0,0,0,0.1)', minWidth: 160 },
          onMouseEnter: handleSubMouseEnter,
          onMouseLeave: handleSubMouseLeave,
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        hideBackdrop
        style={{ pointerEvents: 'none' }}
        MenuListProps={{ style: { pointerEvents: 'auto' } }}
      >
        {FILE_GROUPS.map((group) => (
          <SortMenuItem
            key={group.value}
            selected={value === group.value}
            onClick={() => handleSelect(group.value)}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>{group.icon}</ListItemIcon>
            <ListItemText>{group.label}</ListItemText>
          </SortMenuItem>
        ))}
      </Menu>
    </>
  );
};
